import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import MapTooltip from '../map-tooltip';
import { setActiveRegion, setComparedRegion } from '../../actions/ui';
import bboxArray from 'turf-bbox';
import * as selectors from '../../reducers';
import WebMercatorViewport from 'viewport-mercator-project';
import ReactMapGL, { SVGOverlay, FlyToInterpolator } from 'react-map-gl';
import { parse as SvgParser } from 'svg-parser';
import geojson2svg from 'geojson-to-svg';
import useComponentSize from '@rehooks/component-size';
import styles from './region-map.module.scss';
import { PlusIcon, MinusIcon } from '../icons';

const COLOR_BLUE = 'rgb(200, 204, 230)';
const COLOR_HIGHLIGHT = '#6a74c4';
const DEFAULT_COORDINATES = [-19.5, 146];
const minZoom = 3.895055500278351;

const initialViewport = {
  latitude: DEFAULT_COORDINATES[0],
  longitude: DEFAULT_COORDINATES[1],
  zoom: 4.7,
  width: 200,
  height: 600,
};

const getPathObjects = ({ project, feature }) => {
  const svgString = geojson2svg().projection(project).data(feature).render();
  const svgObj = SvgParser(svgString);
  const gObj = svgObj.children[0];
  const pathObjs = gObj.children;
  return pathObjs;
};

const flyTo = {
  transitionDuration: 700,
  transitionInterpolator: new FlyToInterpolator(),
};

function usePrevious(value) {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
}

const RegionMap = ({
  qldCouncilsGeo,
  council,
  isMobile,
  onCouncilSelect,
  scrollZoom,
}) => {
  const [viewport, setViewport] = useState(initialViewport);
  const [hoveredCouncil, setHoveredCouncil] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const prevIsPanning = usePrevious(isPanning);

  let mapContainerRef = useRef(null);
  let { width, height } = useComponentSize(mapContainerRef);

  const getCouncilBounds = () => {
    const geoCouncil = qldCouncilsGeo.features.find((councilGeo) => {
      return councilGeo.properties['NAME'] === council.id;
    });
    const boundsArray = bboxArray(geoCouncil.geometry);
    return [
      [boundsArray[0], boundsArray[1]],
      [boundsArray[2], boundsArray[3]],
    ];
  };

  const getQldBounds = () => {
    const boundsArray = qldCouncilsGeo.bbox;
    return [
      [boundsArray[0], boundsArray[1]],
      [boundsArray[2], boundsArray[3]],
    ];
  };

  //   When the council changes, set map bounds to the new council
  useEffect(() => {
    if (!width) return;
    // bbox for the region or QLD, depending on whether there's an active region
    const bounds = council ? getCouncilBounds() : getQldBounds();
    const padding = council ? 0.2 : 0;
    const flying = council ? flyTo : {};
    const newViewport = new WebMercatorViewport({
      width: width,
      height: height,
    }).fitBounds(bounds);
    setViewport({
      ...newViewport,
      zoom: newViewport.zoom - padding,
      ...flying,
    });
  }, [council, width, height]);

  return (
    <div
      ref={mapContainerRef}
      style={{ width: '100%', height: '100%', cursor: 'grab' }}
    >
      <ReactMapGL
        scrollZoom={scrollZoom}
        dragPan={true}
        dragRotate={false}
        doubleClickZoom={false}
        touchZoom={isMobile}
        touchRotate={false}
        onViewportChange={(viewport) => setViewport(viewport)}
        initialViewState={initialViewport}
        viewState={viewport}
        width={'100%'}
        onInteractionStateChange={(s) => setIsPanning(s.isDragging)}
        height={'100%'}
        mapStyle={{
          version: 8,
          name: 'Basic',
          sources: {
            neighbours: {
              type: 'geojson',
              data: `${process.env.PUBLIC_URL}/data/neighbours.geo.json`,
            },
          },
          layers: [
            {
              id: 'neighbour-layer',
              type: 'fill',
              source: 'neighbours',
              paint: {
                'fill-color': 'rgb(235, 235, 246)',
              },
            },
          ],
        }}
      >
        <SVGOverlay
          redraw={({ project }) => {
            if (!qldCouncilsGeo) return null;
            const nameSelector = (x) => x.properties.NAME;
            return qldCouncilsGeo.features.map((x, i) => {
              const pathObjs = getPathObjects({ project, feature: x });
              const active =
                nameSelector(x) === hoveredCouncil ||
                (council && nameSelector(x) === council.name);
              return (
                <g key={nameSelector(x)}>
                  {pathObjs.map((path, i) => {
                    return (
                      <path
                        onClick={() => {
                          if (!active) {
                            setHoveredCouncil(nameSelector(x));
                          } else {
                            // hack to not execute a click if the panning just ended
                            if (!prevIsPanning) {
                              onCouncilSelect(nameSelector(x));
                            }
                          }
                        }}
                        onMouseEnter={(e) => {
                          if (isMobile) return;
                          setHoveredCouncil(nameSelector(x));
                        }}
                        onMouseLeave={() => setHoveredCouncil(null)}
                        key={'fill' + i}
                        stroke="white"
                        strokeWidth="1"
                        fill={active ? COLOR_HIGHLIGHT : COLOR_BLUE}
                        style={{
                          cursor: 'pointer',
                        }}
                        d={path.attributes.d}
                      />
                    );
                  })}
                </g>
              );
            });
          }}
        />
      </ReactMapGL>

      <MapTooltip
        visible={Boolean(hoveredCouncil)}
        position={{ x: 0, y: 0 }}
        isStatic={true}
      >
        {hoveredCouncil}
      </MapTooltip>
      <div
        className={styles.navigationControls}
        style={{ position: 'absolute', right: 10, top: 10 }}
      >
        <div>
          <div className="mapboxgl-ctrl mapboxgl-ctrl-group ">
            <button
              onClick={() => {
                const newZoom = viewport.zoom + 1;
                setViewport({ ...viewport, ...flyTo, zoom: newZoom });
              }}
              className="mapboxgl-ctrl-icon mapboxgl-ctrl-zoom-in"
              type="button"
              title="Zoom In"
            >
              <PlusIcon width={12} height={12} />
            </button>
            <button
              onClick={() => {
                const newZoom = viewport.zoom - 1;
                if (newZoom < minZoom) return;
                setViewport({ ...viewport, ...flyTo, zoom: newZoom });
              }}
              className="mapboxgl-ctrl-icon mapboxgl-ctrl-zoom-out"
              type="button"
              disabled={viewport.zoom - 1 < minZoom}
              title="Zoom Out"
            >
              <MinusIcon width={12} height={12} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapDispatchToProps = {
  setActiveRegion,
  setComparedRegion,
};

const mapStateToProps = (state) => ({
  qldCouncilsGeo: selectors.getQldCouncilsGeo(state),
  isMobile: selectors.getIsMobile(state),
});

RegionMap.propTypes = {
  qldCouncilsGeo: PropTypes.object,
  onCouncilSelect: PropTypes.func,
};

export default connect(mapStateToProps, mapDispatchToProps)(RegionMap);



// WEBPACK FOOTER //
// ./src/components/region-map/RegionMap.js